import { supabase } from './supabase';

/**
 * Image Service for handling uploads, compression, and storage
 * Uses Supabase Storage for free, scalable image hosting
 */
export class ImageService {
    constructor() {
        this.bucket = 'images'; // Supabase storage bucket name
        this.cache = new Map(); // In-memory cache for loaded images
    }

    /**
     * Compress and resize image to specified dimensions
     * @param {File} file - Original image file
     * @param {number} maxWidth - Maximum width (default: 200)
     * @param {number} maxHeight - Maximum height (default: 200)
     * @param {number} quality - JPEG quality 0-1 (default: 0.8)
     * @returns {Promise<Blob>} - Compressed image blob
     */
    async compressImage(file, maxWidth = 200, maxHeight = 200, quality = 0.8) {
        return new Promise((resolve) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();

            img.onload = () => {
                // Calculate new dimensions maintaining aspect ratio
                let { width, height } = img;

                if (width > height) {
                    if (width > maxWidth) {
                        height = (height * maxWidth) / width;
                        width = maxWidth;
                    }
                } else {
                    if (height > maxHeight) {
                        width = (width * maxHeight) / height;
                        height = maxHeight;
                    }
                }

                canvas.width = width;
                canvas.height = height;

                // Draw and compress
                ctx.drawImage(img, 0, 0, width, height);

                canvas.toBlob(resolve, 'image/jpeg', quality);
            };

            img.src = URL.createObjectURL(file);
        });
    }

    /**
     * Upload image to Supabase Storage
     * @param {File|Blob} file - Image file to upload
     * @param {string} folder - Storage folder (organization, center, class, user)
     * @param {string} id - Unique identifier for the image
     * @returns {Promise<{success: boolean, url?: string, error?: string}>}
     */
    async uploadImage(file, folder, id) {
        try {
            // Compress image first
            const compressedImage = await this.compressImage(file);

            // Generate unique filename
            const fileExt = 'jpg'; // Always save as JPEG after compression
            const fileName = `${folder}/${id}-${Date.now()}.${fileExt}`;

            // Upload to Supabase Storage
            const { error } = await supabase.storage
                .from(this.bucket)
                .upload(fileName, compressedImage, {
                    contentType: 'image/jpeg',
                    upsert: true // Replace if exists
                });

            if (error) {
                return { success: false, error: error.message };
            }

            // Get public URL
            const { data: urlData } = supabase.storage
                .from(this.bucket)
                .getPublicUrl(fileName);

            return { success: true, url: urlData.publicUrl };
        } catch (error) {
            console.error('Image upload failed:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Delete image from Supabase Storage
     * @param {string} imageUrl - Full image URL to delete
     * @returns {Promise<boolean>} - Success status
     */
    async deleteImage(imageUrl) {
        try {
            if (!imageUrl) return true;

            // Extract file path from URL
            const urlParts = imageUrl.split('/');
            const fileName = urlParts.slice(-2).join('/'); // folder/filename

            const { error } = await supabase.storage
                .from(this.bucket)
                .remove([fileName]);

            if (error) {
                console.error('Delete error:', error);
                return false;
            }

            // Remove from cache
            this.cache.delete(imageUrl);
            return true;
        } catch (error) {
            console.error('Image deletion failed:', error);
            return false;
        }
    }

    /**
     * Get cached image or load and cache it
     * @param {string} imageUrl - Image URL to load
     * @returns {Promise<string>} - Image URL (cached or original)
     */
    async getCachedImage(imageUrl) {
        if (!imageUrl) return null;

        // Return from cache if available
        if (this.cache.has(imageUrl)) {
            return this.cache.get(imageUrl);
        }

        // Load image and add to cache
        try {
            const img = new Image();
            img.src = imageUrl;

            await new Promise((resolve, reject) => {
                img.onload = resolve;
                img.onerror = reject;
            });

            this.cache.set(imageUrl, imageUrl);
            return imageUrl;
        } catch (error) {
            console.error('Failed to load image:', error);
            return null;
        }
    }

    /**
     * Get default image for entity type
     * @param {string} type - Entity type (organization, center, class, user)
     * @returns {string} - Default image path
     */
    getDefaultImage(type) {
        const defaults = {
            organization: '/images/defaults/organization.svg',
            center: '/images/defaults/center.svg',
            class: '/images/defaults/class.svg',
            user: '/images/defaults/user.svg',
            manager: '/images/defaults/manager.svg',
            coach: '/images/defaults/coach.svg',
            parent: '/images/defaults/parent.svg',
            student: '/images/defaults/student.svg'
        };

        return defaults[type] || defaults.organization;
    }

    /**
     * Clear image cache (useful for memory management)
     */
    clearCache() {
        this.cache.clear();
    }

    /**
     * Validate image file
     * @param {File} file - File to validate
     * @returns {{valid: boolean, error?: string}}
     */
    validateImageFile(file) {
        const maxSize = 5 * 1024 * 1024; // 5MB
        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];

        if (!allowedTypes.includes(file.type)) {
            return { valid: false, error: 'Please select a JPEG, PNG, or WebP image.' };
        }

        if (file.size > maxSize) {
            return { valid: false, error: 'Image size must be less than 5MB.' };
        }

        return { valid: true };
    }
}

// Export singleton instance
export const imageService = new ImageService();